import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { TextArea } from '@shared/components/ui/textarea';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUpload, faTrash } from '@fortawesome/pro-light-svg-icons';
import { useCountries } from '@shared/hooks/useCountries';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { useBankAccountMutations } from '../../hooks/useBankAccountMutations';

export interface CreateBankAccountModalRef {
  open: () => void;
  close: () => void;
}

interface CreateBankAccountModalProps {
  onSuccess?: () => void;
}

/** Códigos ISO 4217 ofrecidos al crear cuenta; si la moneda del equipo no está, se incluye igualmente. */
const CREATE_BANK_ACCOUNT_CURRENCY_CODES = [
  'EUR',
  'MXN',
  'USD',
  'GBP',
  'COP',
  'CLP',
  'PEN',
  'ARS',
  'BRL',
  'CHF',
  'PLN',
  'RON',
  'SEK',
] as const;

export const CreateBankAccountModal = forwardRef<CreateBankAccountModalRef, CreateBankAccountModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('bankAccountsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { createBankAccount } = useBankAccountMutations();
    const { countries, loading: countriesLoading } = useCountries();
    const teamCurrency = useCurrency();

    const countryOptions = useMemo(() => countries.map((c) => ({ label: c.name, value: c.code })), [countries]);

    const currencyOptions = useMemo(() => {
      const normalized = teamCurrency.trim().toUpperCase();
      const base = [...CREATE_BANK_ACCOUNT_CURRENCY_CODES] as string[];
      if (normalized && !base.includes(normalized)) {
        base.unshift(normalized);
      }
      return base;
    }, [teamCurrency]);

    // Form state
    const [holderName, setHolderName] = useState('');
    const [iban, setIban] = useState('');
    const [bic, setBic] = useState('');
    const [bankName, setBankName] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [currencyCode, setCurrencyCode] = useState(() => teamCurrency.trim().toUpperCase() || 'EUR');
    const [internalNote, setInternalNote] = useState('');
    const [proofDocument, setProofDocument] = useState<File | null>(null);

    const resetForm = () => {
      setHolderName('');
      setIban('');
      setBic('');
      setBankName('');
      setCountryCode('');
      setCurrencyCode(teamCurrency.trim().toUpperCase() || 'EUR');
      setInternalNote('');
      setProofDocument(null);
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        resetForm();
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // Validar tamaño (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
          toast({
            title: 'Error',
            description: 'El archivo no puede superar los 2MB',
            variant: 'destructive',
          });
          return;
        }
        // Validar tipo
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!validTypes.includes(file.type)) {
          toast({
            title: 'Error',
            description: 'Solo se permiten archivos PDF, JPG o PNG',
            variant: 'destructive',
          });
          return;
        }
        setProofDocument(file);
      }
    };

    const handleRemoveFile = () => {
      setProofDocument(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleSubmit = async () => {
      try {
        await createBankAccount.mutateAsync({
          holder_name: holderName,
          iban: iban.replace(/\s/g, ''),
          bic: bic,
          bank_name: bankName,
          country_code: countryCode,
          currency: currencyCode.trim().toUpperCase(),
          internal_note: internalNote || undefined,
          proof_document_file: proofDocument || undefined,
        });

        toast({
          title: t('modals.create.success.title'),
          description: t('modals.create.success.description'),
        });

        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.createFailed'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isFormValid =
      holderName.trim() && iban.trim() && bic.trim() && bankName.trim() && countryCode && currencyCode.trim();

    const isLoading = createBankAccount.isPending || countriesLoading;

    return (
      <ModalCenter ref={modalRef} title={t('modals.create.title')} width="lg" className="max-h-[90vh]">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('modals.create.description')}</p>

          {/* Holder Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.holder_name')} *</label>
            <Input
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              placeholder={t('modals.create.fields.holder_name_placeholder')}
              disabled={isLoading}
            />
          </div>

          {/* IBAN */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.iban')} *</label>
            <Input
              value={iban}
              onChange={(e) => setIban(e.target.value.toUpperCase())}
              placeholder={t('modals.create.fields.iban_placeholder')}
              disabled={isLoading}
              className="font-mono"
            />
          </div>

          {/* BIC */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.bic')} *</label>
            <Input
              value={bic}
              onChange={(e) => setBic(e.target.value.toUpperCase())}
              placeholder={t('modals.create.fields.bic_placeholder')}
              disabled={isLoading}
              className="font-mono"
            />
          </div>

          {/* Bank Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.bank_name')} *</label>
            <Input
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder={t('modals.create.fields.bank_name_placeholder')}
              disabled={isLoading}
            />
          </div>

          {/* Country */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.country_code')} *</label>
            <Combobox
              options={countryOptions}
              placeholder={t('modals.create.fields.country_code_placeholder')}
              value={countryCode}
              setValue={setCountryCode}
              className="w-full"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.currency')} *</label>
            <Select value={currencyCode} onValueChange={setCurrencyCode} disabled={isLoading}>
              <SelectTrigger className="w-full" data-testid="create-bank-account-currency-select">
                <SelectValue placeholder={t('modals.create.fields.currency_placeholder')} />
              </SelectTrigger>
              <SelectContent className="max-h-[min(50vh,16rem)] z-[120]">
                {currencyOptions.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Internal Note */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.internal_note')}</label>
            <TextArea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder={t('modals.create.fields.internal_note_placeholder')}
              disabled={isLoading}
              rows={2}
            />
          </div>

          {/* Proof Document Upload */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.proof_document')}</label>
            <p className="text-xs text-muted-foreground mb-2">{t('modals.create.fields.proof_document_description')}</p>

            {!proofDocument ? (
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faCloudUpload} className="w-8 h-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{t('upload.dropzone')}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('upload.limit')}</p>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm truncate">{proofDocument.name}</span>
                <Button variant="ghost" size="sm" onClick={handleRemoveFile} disabled={isLoading}>
                  <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                </Button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid || isLoading}>
              {isLoading ? t('buttons.creating') : t('buttons.confirm')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CreateBankAccountModal.displayName = 'CreateBankAccountModal';
